import React, { useContext, useState, useEffect, createContext, useCallback, useMemo } from 'react';
import {
  browserSessionPersistence,
  onAuthStateChanged,
  setPersistence,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { auth as firebaseAuth, firebaseApp, viewAsApp, viewAsAuth } from '../firebase';
import { decodeClaims } from '../permissions';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

async function withClaims(user) {
  if (!user) return null;
  await user.getIdToken(true);
  user.customClaims = decodeClaims((await user.getIdTokenResult()).claims);
  return user;
}

export function AuthProvider({ children }) {
  const [adminUser, setAdminUser] = useState(null);
  const [viewAsUser, setViewAsUser] = useState(null);
  const [adminReady, setAdminReady] = useState(false);
  const [viewAsReady, setViewAsReady] = useState(false);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    const unsubscribeAdmin = onAuthStateChanged(firebaseAuth, async (user) => {
      try {
        setAuthError('');
        setAdminUser(await withClaims(user));
      } catch (error) {
        console.error('Admin auth state resolution failed:', error);
        setAuthError(error.message || 'Authentication failed to load.');
        setAdminUser(null);
      } finally {
        setAdminReady(true);
      }
    });
    const unsubscribeViewAs = onAuthStateChanged(viewAsAuth, async (user) => {
      try {
        const resolved = await withClaims(user);
        const expiresAtMs = Number(resolved?.customClaims?.viewAsExpiresAtMs);
        if (resolved && (!resolved.customClaims?.viewAsSessionId || !Number.isFinite(expiresAtMs) || expiresAtMs <= Date.now())) {
          await signOut(viewAsAuth);
          setViewAsUser(null);
        } else {
          setViewAsUser(resolved);
        }
      } catch (error) {
        console.error('View as auth state resolution failed:', error);
        setViewAsUser(null);
        await signOut(viewAsAuth).catch(() => {});
      } finally {
        setViewAsReady(true);
      }
    });
    return () => { unsubscribeAdmin(); unsubscribeViewAs(); };
  }, []);

  useEffect(() => {
    if (!adminReady || !viewAsReady || !viewAsUser) return;
    if (adminUser?.customClaims?.admin !== true || viewAsUser.customClaims?.viewAsActorUid !== adminUser.uid) {
      signOut(viewAsAuth).catch(console.error);
      setViewAsUser(null);
    }
  }, [adminReady, viewAsReady, adminUser, viewAsUser]);

  useEffect(() => {
    const expiresAtMs = Number(viewAsUser?.customClaims?.viewAsExpiresAtMs);
    if (!Number.isFinite(expiresAtMs)) return;
    const remaining = expiresAtMs - Date.now();
    if (remaining <= 0) {
      signOut(viewAsAuth).catch(console.error);
      setViewAsUser(null);
      return;
    }
    const timer = window.setTimeout(() => {
      signOut(viewAsAuth).catch(console.error);
      setViewAsUser(null);
    }, remaining);
    return () => window.clearTimeout(timer);
  }, [viewAsUser]);

  const isImpersonating = !!(adminUser?.customClaims?.admin && viewAsUser?.customClaims?.viewAsActorUid === adminUser.uid);
  const currentUser = isImpersonating ? viewAsUser : adminUser;
  const loading = !adminReady || !viewAsReady;

  const login = useCallback((email, password) => signInWithEmailAndPassword(firebaseAuth, email, password), []);

  const startViewAs = useCallback(async (uid) => {
    if (adminUser?.customClaims?.admin !== true || viewAsUser) throw new Error('Admin access required.');
    const token = await adminUser.getIdToken();
    const response = await fetch('/api/v1/admin/view-as', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) throw new Error(data.message || 'Could not start View as.');
    try {
      await setPersistence(viewAsAuth, browserSessionPersistence);
      const credential = await signInWithCustomToken(viewAsAuth, data.token);
      const target = await withClaims(credential.user);
      if (target.customClaims?.viewAsSessionId !== data.sessionId || target.customClaims?.viewAsActorUid !== adminUser.uid || Number(target.customClaims?.viewAsExpiresAtMs) !== data.expiresAtMs) {
        throw new Error('View as session could not be verified.');
      }
      setViewAsUser(target);
    } catch (error) {
      await signOut(viewAsAuth).catch(() => {});
      fetch(`/api/v1/admin/view-as/${encodeURIComponent(data.sessionId)}/end`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
      throw error;
    }
  }, [adminUser, viewAsUser]);

  const stopViewAs = useCallback(async () => {
    const sessionId = viewAsUser?.customClaims?.viewAsSessionId;
    if (!sessionId || !adminUser) return;
    const token = await adminUser.getIdToken();
    const response = await fetch(`/api/v1/admin/view-as/${encodeURIComponent(sessionId)}/end`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) throw new Error(data.message || 'Could not end View as.');
    await signOut(viewAsAuth);
    setViewAsUser(null);
  }, [adminUser, viewAsUser]);

  const logout = useCallback(async () => {
    if (viewAsUser) await signOut(viewAsAuth);
    await signOut(firebaseAuth);
  }, [viewAsUser]);

  const canAccessPage = useCallback((pageId) => {
    if (currentUser?.customClaims?.admin) return true;
    return currentUser?.customClaims?.permissions?.pages?.includes(pageId) || false;
  }, [currentUser]);

  const canPerformAction = useCallback((actionId) => {
    if (currentUser?.customClaims?.admin) return true;
    return currentUser?.customClaims?.permissions?.actions?.includes(actionId) || false;
  }, [currentUser]);

  const value = useMemo(() => ({
    currentUser,
    actorUser: adminUser,
    isAdmin: currentUser?.customClaims?.admin === true,
    isImpersonating,
    activeFirebaseApp: isImpersonating ? viewAsApp : firebaseApp,
    loading,
    canAccessPage,
    canPerformAction,
    startViewAs,
    stopViewAs,
    login,
    logout,
  }), [currentUser, adminUser, isImpersonating, loading, canAccessPage, canPerformAction, startViewAs, stopViewAs, login, logout]);

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div style={{ padding: '2rem', textAlign: 'center' }}>Loading application...</div>
      ) : authError ? (
        <div style={{ padding: '2rem', color: '#9d2e2e' }}>
          Authentication failed to load: {authError}
        </div>
      ) : children}
    </AuthContext.Provider>
  );
}
